
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DuplicateGame {
  id: string;
  title: string;
  thumbnail: string;
  category: string;
  created_at: string;
  play_count: number;
  fake_play_count: number;
}

interface DuplicateGroup {
  title: string;
  games: DuplicateGame[];
}

// Helper function to normalize titles for comparison
const normalizeTitle = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    // Remove common punctuation and special characters
    .replace(/[^\w\s]/g, '')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    .trim();
};

// Helper function to calculate similarity between two strings
const calculateSimilarity = (str1: string, str2: string): number => {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const distance = levenshteinDistance(longer, shorter);
  return (longer.length - distance) / longer.length;
};

// Levenshtein distance calculation
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
};

export const useAdminGamesDuplicates = () => {
  const [isDetecting, setIsDetecting] = useState(false);
  const queryClient = useQueryClient();

  // Detect duplicates based on title similarity
  const { data: duplicateGroups = [], isLoading } = useQuery({
    queryKey: ['admin-games-duplicates'],
    queryFn: async () => {
      console.log('Starting duplicate detection...');
      
      const { data: games, error } = await supabase
        .from('games')
        .select('id, title, thumbnail, category, created_at, play_count, fake_play_count')
        .order('title');

      if (error) throw error;

      console.log(`Found ${games.length} games to analyze`);

      // Group games by normalized title and similarity
      const duplicateGroups: DuplicateGroup[] = [];
      const processedGames = new Set<string>();
      
      for (let i = 0; i < games.length; i++) {
        const currentGame = games[i];
        
        if (processedGames.has(currentGame.id)) continue;
        
        const similarGames: DuplicateGame[] = [currentGame];
        const normalizedCurrentTitle = normalizeTitle(currentGame.title);
        
        // Find similar games
        for (let j = i + 1; j < games.length; j++) {
          const compareGame = games[j];
          
          if (processedGames.has(compareGame.id)) continue;
          
          const normalizedCompareTitle = normalizeTitle(compareGame.title);
          
          // Check for exact match after normalization
          const isExactMatch = normalizedCurrentTitle === normalizedCompareTitle;
          
          // Check for high similarity (85% or higher)
          const similarity = calculateSimilarity(normalizedCurrentTitle, normalizedCompareTitle);
          const isSimilar = similarity >= 0.85;
          
          console.log(`Comparing "${currentGame.title}" vs "${compareGame.title}":`, {
            normalizedCurrent: normalizedCurrentTitle,
            normalizedCompare: normalizedCompareTitle,
            similarity: similarity,
            isExactMatch,
            isSimilar
          });
          
          if (isExactMatch || isSimilar) {
            similarGames.push(compareGame);
            processedGames.add(compareGame.id);
          }
        }
        
        // Only add to duplicates if there are multiple games
        if (similarGames.length > 1) {
          duplicateGroups.push({
            title: currentGame.title,
            games: similarGames.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          });
          
          console.log(`Found duplicate group for "${currentGame.title}" with ${similarGames.length} games`);
        }
        
        processedGames.add(currentGame.id);
      }

      console.log(`Detection complete. Found ${duplicateGroups.length} duplicate groups`);
      return duplicateGroups;
    },
    enabled: isDetecting,
  });

  // Delete duplicate games mutation
  const deleteDuplicatesMutation = useMutation({
    mutationFn: async (gameIds: string[]) => {
      const { error } = await supabase
        .from('games')
        .delete()
        .in('id', gameIds);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games-duplicates'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      toast.success('Duplicate games deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete duplicate games: ' + error.message);
    },
  });

  const startDetection = () => {
    console.log('Starting duplicate detection...');
    setIsDetecting(true);
  };

  const stopDetection = () => {
    console.log('Stopping duplicate detection...');
    setIsDetecting(false);
  };

  return {
    duplicateGroups,
    isLoading: isLoading && isDetecting,
    isDetecting,
    startDetection,
    stopDetection,
    deleteDuplicatesMutation
  };
};
